import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const CategoryDropdown = ({ id, listId, value, options, onSelect, questionLength }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleSelect = (option) => {
    setSelected(option);
    setOpen(false);
    onSelect(option);
  };

  useEffect(() => {
    setSelected(value ?? null);
  }, [value]);

  return (
    <DropdownWrapper>
      <DropdownButton
        onClick={() => setOpen((v) => !v)}
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <ButtonText title={selected?.name || ''}>
          {selected?.name || 'Select…'}
        </ButtonText>
        <Chevron open={open} />
      </DropdownButton>

      <DropdownContent
        open={open}
        id={listId}
        questionLength={questionLength}
        role="listbox"
      >
        {options.map((option, index) => (
          <DropdownItem
            key={index}
            role="option"
            aria-selected={selected?.name === option?.name}
            onMouseDown={(e) => {
              // Only prevent default on mouse events, not touch
              if (e.button !== undefined) e.preventDefault();
            }}
            onClick={() => handleSelect(option)}
          >
            {option.name}
          </DropdownItem>
        ))}
      </DropdownContent>
    </DropdownWrapper>
  );
};

export default CategoryDropdown;

const DropdownWrapper = styled.div`
  text-align: left;
  position: relative;
  display: inline-block;
  width: 100%;
  font-family: 'Raleway';
  touch-action: auto; /* Allow all touch gestures */
`;

const DropdownButton = styled.button`
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  width: 100%;
  min-height: 40px;         /* stable height */
  padding: 8px 12px;
  background: transparent;
  border: none;
  border-bottom: 1px solid blue;
  cursor: pointer;

  color: #666D79;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;

  box-sizing: border-box;
`;

const ButtonText = styled.span`
  text-align: left;
  flex: 1 1 auto;
  min-width: 0;             /* allow ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
`;

const DropdownContent = styled.div`
  text-align: left;
  pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
  visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
  opacity: ${({ open }) => (open ? 1 : 0)};
  transform: ${({ open }) => (open ? 'translateY(0)' : 'translateY(-4px)')};
  transition:
    opacity 120ms ease,
    transform 120ms ease,
    visibility 0ms linear ${({ open }) => (open ? '0ms' : '120ms')};

  position: absolute;
  top: calc(100% + 6px);    /* always below button, no overlap upward */
  left: 0;
  right: 0;

  background-color: #fff;
  min-width: 100%;
  box-shadow: 0px 8px 16px rgba(0,0,0,0.12);
  border-radius: 6px;
  z-index: 10;

  max-height: 225px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* Enable momentum scrolling on iOS */
  overscroll-behavior: contain;       /* Prevent parent scroll during dropdown scroll */
  touch-action: pan-y;                /* Explicitly allow vertical touch scrolling */

  @media (max-height: 775px) { max-height: 175px; }
  @media (max-height: 700px) { max-height: 150px; }
  @media (max-height: 650px) { max-height: 125px; }
  @media (max-height: 600px) { max-height: 75px; }
`;

const DropdownItem = styled.div`
  text-align: left;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
  &:hover { background-color: #f7f7f7; }
`;

const Chevron = styled.i`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border: solid #666D79;
  border-width: 0 2px 2px 0;
  display: inline-block;
  transform: ${({ open }) => (open ? 'rotate(-135deg)' : 'rotate(45deg)')};
  transition: transform 120ms ease;
`;
